import React from "react";

import { useSpring, useTransition } from "react-spring";

import { ANIM_CONFIG } from "../constants";
import { TAnimatedStyle, TRenderableRow } from "../types";

/**
 * Props for the useTimelineAnimations hook
 */
type UseTimelineAnimationsProps = {
  /** Array of rows to animate */
  renderableRows: TRenderableRow[];
  /** Total height of all rows in pixels */
  totalHeight: number;
};

/**
 * Manages react-spring animations for timeline rows and total height.
 *
 * This hook creates smooth animations for:
 * - Row enter/exit/update transitions (opacity, height, y position)
 * - Total height changes (for container resizing)
 * - Collection expand/collapse effects
 *
 * @param props - Hook configuration
 * @returns Animated total height and transitions render function
 */
export const useTimelineAnimations = ({ renderableRows, totalHeight }: UseTimelineAnimationsProps) => {
  const { animatedTotalHeight } = useSpring({
    animatedTotalHeight: totalHeight,
    config: ANIM_CONFIG,
  });

  const transitionsResult = useTransition(renderableRows, {
    key: (row: TRenderableRow) => row.id,
    from: (row: TRenderableRow) => ({
      opacity: 0,
      height: 0,
      y: row.y,
    }),
    enter: (row: TRenderableRow) => ({
      opacity: 1,
      height: row.height,
      y: row.y,
    }),
    update: (row: TRenderableRow) => ({
      opacity: 1,
      height: row.height,
      y: row.y,
    }),
    leave: (row: TRenderableRow) => ({
      opacity: 0,
      height: 0,
      y: row.y - row.height,
    }),
    config: ANIM_CONFIG,
  });

  const transitions = (Array.isArray(transitionsResult) ? transitionsResult[0] : transitionsResult) as unknown as (
    render: (style: TAnimatedStyle, item: TRenderableRow | null) => React.ReactNode | null,
  ) => React.ReactNode;

  return {
    animatedTotalHeight,
    transitions,
  };
};
